import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

const statusMap = {
  Planlandı: 'Planned',
  Tamamlandı: 'Completed',
  'İptal Edildi': 'Cancelled',
  Ertelendi: 'Postponed',
  'Yüz Yüze': 'Face to Face',
  Telefon: 'Phone',
  'Video Konferans': 'Video Conference',
  'E-posta': 'Email',
  Başarılı: 'Successful',
  Başarısız: 'Unsuccessful',
  Beklemede: 'Pending',
  'Takip Gerekli': 'Follow-up Required',
};

const label = (v) => (v ? statusMap[v] || v : '—');

const formatDateTime = (v) => {
  if (!v) return '—';
  const s = typeof v === 'string' ? v.replace(' ', 'T') : v;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDateOnly = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

const hotelDisplay = (call) => {
  if (call.hotel_name) return call.hotel_name;
  if (call.hotel && typeof call.hotel === 'object' && call.hotel.hotel_name) return call.hotel.hotel_name;
  if (call.hotel != null && call.hotel !== '') return String(call.hotel);
  return '—';
};

function Kv({ k, v, fullWidth }) {
  return (
    <div className={`sc-kv ${fullWidth ? 'sc-kv-full' : ''}`}>
      <span className="sc-kv-k">{k}</span>
      <span className="sc-kv-v">{v ?? '—'}</span>
    </div>
  );
}

function Card({ title, children, className = '', compact }) {
  return (
    <section className={`sc-card ${compact ? 'sc-card--compact' : ''} ${className}`.trim()}>
      <div className="sc-card-head">
        <span className="sc-card-head-bar" aria-hidden />
        <h2 className="sc-card-title">{title}</h2>
      </div>
      <div className="sc-card-body">{children}</div>
    </section>
  );
}

function Badge({ children, muted }) {
  return <span className={`sc-badge ${muted ? 'sc-badge-muted' : ''}`.trim()}>{children}</span>;
}

/**
 * One sales call per printed page. Card-based layout for PDF / print.
 */
function SalesCallPrintSheet({ calls, onAfterPrint }) {
  useEffect(() => {
    if (!calls?.length) return undefined;

    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      onAfterPrint?.();
    };

    const printTimer = window.setTimeout(() => {
      window.print();
    }, 200);

    window.addEventListener('afterprint', finish);
    const fallbackTimer = window.setTimeout(finish, 120000);

    return () => {
      window.clearTimeout(printTimer);
      window.clearTimeout(fallbackTimer);
      window.removeEventListener('afterprint', finish);
    };
  }, [calls, onAfterPrint]);

  if (!calls?.length) return null;

  const sheet = (
    <>
      <style>{`
        @media print {
          @page { size: A4; margin: 10mm; }
          body * { visibility: hidden !important; }
          #sales-call-print-root,
          #sales-call-print-root * { visibility: visible !important; }
          #sales-call-print-root {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            background: #f3f4f6 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .sc-card,
          .sc-hero,
          .sc-badge,
          .sc-text-card,
          .sc-kv-v {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
        #sales-call-print-root {
          position: fixed;
          left: -9999px;
          top: 0;
          width: 210mm;
          font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
          color: #111827;
          font-size: 12px;
          line-height: 1.5;
          background: #f3f4f6;
        }
        /* Only force a break *between* meetings — not after the last one (avoids trailing blank page). */
        .sc-print-page:not(:last-child) {
          page-break-after: always;
        }
        .sc-print-page {
          box-sizing: border-box;
          padding: 0 0 4mm 0;
        }
        .sc-page-inner {
          max-width: 190mm;
          margin: 0 auto;
        }
        .sc-hero {
          background: linear-gradient(135deg, #1e40af 0%, #1d4ed8 48%, #2563eb 100%);
          color: #fff;
          border-radius: 12px;
          padding: 14px 18px 16px;
          margin-bottom: 10px;
          box-shadow: 0 4px 14px rgba(29, 78, 216, 0.35);
        }
        .sc-hero-kicker {
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          opacity: 0.92;
          margin: 0 0 6px 0;
        }
        .sc-hero-title {
          font-size: 20px;
          font-weight: 800;
          margin: 0 0 8px 0;
          letter-spacing: -0.02em;
        }
        .sc-hero-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 8px 16px;
          font-size: 11px;
          opacity: 0.95;
        }
        .sc-hero-meta span {
          background: rgba(255,255,255,0.15);
          padding: 4px 10px;
          border-radius: 999px;
        }
        .sc-cards-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
          margin-bottom: 10px;
          align-items: stretch;
        }
        .sc-card {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06);
          min-height: 0;
          height: 100%;
          display: flex;
          flex-direction: column;
        }
        .sc-card-head {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 11px 14px 9px;
          background: linear-gradient(to bottom, #fafafa, #fff);
          border-bottom: 1px solid #f3f4f6;
          flex-shrink: 0;
        }
        .sc-card-head-bar {
          width: 4px;
          height: 15px;
          border-radius: 2px;
          background: #2563eb;
          flex-shrink: 0;
        }
        .sc-card-title {
          margin: 0;
          font-size: 12.5px;
          font-weight: 700;
          color: #1f2937;
          letter-spacing: 0.02em;
        }
        .sc-card-body {
          padding: 11px 14px 13px;
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          min-height: 0;
        }
        .sc-card-body--badges {
          display: flex;
          flex-wrap: wrap;
          gap: 7px 8px;
          align-items: center;
          align-content: flex-start;
        }
        .sc-card--compact .sc-card-head {
          padding: 9px 12px 7px;
        }
        .sc-card--compact .sc-card-head-bar {
          height: 13px;
        }
        .sc-card--compact .sc-card-title {
          font-size: 12px;
        }
        .sc-card--compact .sc-card-body {
          padding: 9px 12px 11px;
        }
        .sc-card--compact .sc-card-body--badges {
          gap: 6px 8px;
        }
        .sc-card--compact .sc-badge {
          padding: 4px 9px;
          font-size: 10px;
          border-radius: 6px;
        }
        .sc-card--compact .sc-kv {
          padding: 5px 0;
          gap: 8px 10px;
        }
        .sc-card--compact .sc-kv-k {
          font-size: 10.5px;
        }
        .sc-card--compact .sc-kv-v {
          font-size: 12px;
        }
        .sc-badge {
          display: inline-block;
          padding: 4px 11px;
          border-radius: 6px;
          font-size: 10.5px;
          font-weight: 600;
          background: #eff6ff;
          color: #1e40af;
          border: 1px solid #bfdbfe;
        }
        .sc-badge-muted {
          background: #f9fafb;
          color: #4b5563;
          border-color: #e5e7eb;
        }
        .sc-kv {
          display: grid;
          grid-template-columns: 104px 1fr;
          gap: 8px 12px;
          padding: 7px 0;
          border-bottom: 1px solid #f3f4f6;
          align-items: start;
        }
        .sc-kv:last-child {
          border-bottom: none;
          padding-bottom: 0;
        }
        .sc-kv-full {
          grid-column: 1 / -1;
        }
        .sc-kv-k {
          font-weight: 600;
          color: #6b7280;
          font-size: 10.5px;
        }
        .sc-kv-v {
          color: #111827;
          word-break: break-word;
          font-weight: 500;
          font-size: 12px;
        }
        .sc-text-card {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          margin-bottom: 10px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06);
        }
        .sc-text-card .sc-card-head {
          padding-bottom: 6px;
        }
        .sc-text-card .sc-card-title {
          font-size: 12.5px;
        }
        .sc-text-body {
          padding: 0 14px 14px;
        }
        .sc-text-area {
          white-space: pre-wrap;
          margin: 0;
          padding: 12px 14px;
          background: #f9fafb;
          border: 1px solid #f3f4f6;
          border-radius: 8px;
          color: #374151;
          font-size: 15px;
          line-height: 1.62;
          min-height: 56px;
        }
        .sc-attach-list {
          margin: 0;
          padding: 0 12px 12px;
          list-style: none;
        }
        .sc-attach-list li {
          padding: 8px 10px;
          margin-bottom: 6px;
          background: #f9fafb;
          border: 1px solid #f3f4f6;
          border-radius: 8px;
          font-size: 11px;
          color: #374151;
        }
        .sc-attach-list li:last-child {
          margin-bottom: 0;
        }
        .sc-attach-list li::before {
          content: '▸ ';
          color: #2563eb;
          font-weight: bold;
        }
      `}</style>
      <div id="sales-call-print-root">
        {calls.map((call, idx) => (
          <div key={call.id ?? idx} className="sc-print-page">
            <div className="sc-page-inner">
              <header className="sc-hero">
                <p className="sc-hero-kicker">B2B Meetings &amp; Activities</p>
                <h1 className="sc-hero-title">Meeting record</h1>
                <div className="sc-hero-meta">
                  <span>ID #{call.id ?? '—'}</span>
                  {call.created_at ? <span>Record date · {formatDateOnly(call.created_at)}</span> : null}
                </div>
              </header>

              <div className="sc-cards-grid">
                <Card title="People &amp; contact">
                  <Kv k="Agency" v={call.agency} />
                  <Kv k="Contact" v={call.agency_contact} />
                  <Kv k="Phone" v={call.phone} />
                  <Kv k="Representative" v={call.sales_representative} />
                  <Kv k="Guest ID" v={call.guest_id} />
                </Card>

                <Card title="Location">
                  <Kv k="Country" v={call.country} />
                  <Kv k="City" v={call.city} />
                  <Kv k="Address" v={call.address} fullWidth />
                  <Kv k="Hotel" v={hotelDisplay(call)} />
                  <Kv k="Pax" v={call.pax != null ? String(call.pax) : '—'} />
                </Card>
              </div>

              <div className="sc-cards-grid">
                <Card title="Meeting type &amp; outcome" compact>
                  <div className="sc-card-body--badges">
                    <Badge>{label(call.type)}</Badge>
                    <Badge muted>{label(call.communication_type)}</Badge>
                    <Badge muted>{label(call.call_status)}</Badge>
                    <Badge>{label(call.call_result)}</Badge>
                  </div>
                </Card>

                <Card title="Schedule" compact>
                  <Kv k="Start" v={formatDateTime(call.start_date)} />
                  <Kv k="End" v={formatDateTime(call.end_date)} />
                  {call.related_call ? <Kv k="Related call ID" v={String(call.related_call)} /> : null}
                </Card>
              </div>

              <div className="sc-text-card">
                <div className="sc-card-head">
                  <span className="sc-card-head-bar" aria-hidden />
                  <h2 className="sc-card-title">Summary</h2>
                </div>
                <div className="sc-text-body">
                  <p className="sc-text-area">{call.summary || '—'}</p>
                </div>
              </div>

              {Array.isArray(call.attachments) && call.attachments.length > 0 ? (
                <div className="sc-text-card">
                  <div className="sc-card-head">
                    <span className="sc-card-head-bar" aria-hidden />
                    <h2 className="sc-card-title">Attachments</h2>
                  </div>
                  <ul className="sc-attach-list">
                    {call.attachments.map((a) => (
                      <li key={a.id}>{a.file_name || 'file'}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </>
  );

  return createPortal(sheet, document.body);
}

export default SalesCallPrintSheet;
